import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { httpClient } from '../../shared/api/httpClient'
import { useAstreaEnvironment } from '../../shared/hooks/useAstreaEnvironment'
import { adjustTenantsCount, applyFeatureEdit, insertCreatedFeature } from './featureToggleCache'

function isSuccessStatus(status) {
  return status >= 200 && status < 300
}

/**
 * Port of `app/featuretoggle/featureToggleService.js` (AngularJS
 * `FeatureToggleService`) data-fetching logic to TanStack Query, per task
 * 016's requirements.
 *
 * Unlike most `server_app` routes proxied by `httpClient`'s relative paths
 * (e.g. `/users/getUser`, `/banner/info/:env`), this module's endpoints are
 * called directly against the Astrea backend itself
 * (`AuthService.getUrlAstreaPath() + '/api/v2/feature-toggle...'`), same as
 * several other legacy services confirmed by reading the AngularJS sources
 * (`migrationService.js`, `discountCouponsService.js`,
 * `logoutUsersService.js`, `historiesService.js`, `clippingsListService.js`,
 * `actionAuditService.js`). There is no `/api/v2/feature-toggle` route in
 * `server_app/routes/` (confirmed via `grep -rln "feature-toggle" server_app/`
 * returning nothing) — the backend for this module is the Astrea instance
 * itself, not `server_app`. `httpClient` is still reused here (not a second
 * axios instance) since passing an absolute URL to an axios request
 * overrides its configured `baseURL` the same way `$http({ url: ... })` did
 * for the legacy `$http` service, and this keeps the same Basic
 * Auth/X-Session-Id header interceptors applied.
 *
 * `getUrlAstrea()` mirrors `AuthService.getUrlAstreaPath()` by reading the
 * session's `urlAstrea` value straight from `AuthContext` (task 009) —
 * `astreaEnvironments.js`'s `getUrlAstreaPath()` helper resolves the human
 * label (e.g. `"HOMOLOG"`) into the actual base URL, exactly like the
 * legacy helper of the same name did from its own `urlAstrea` cookie.
 */

export function useFeatureTogglesQuery(filter = 'ALL', { enabled = true } = {}) {
  const { urlAstrea, urlAstreaPath: baseUrl } = useAstreaEnvironment()

  const query = useQuery({
    queryKey: ['featureToggles', urlAstrea, filter],
    queryFn: async () => {
      const response = await httpClient.get(`${baseUrl}/api/v2/feature-toggle`, {
        params: { filter },
      })
      return response.data
    },
    enabled: !!urlAstrea && enabled,
  })

  return {
    features: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  }
}

/**
 * Equivalent of the legacy `FeatureToggleService.toggle({ operation, feature,
 * tenantId })`: `POST`/`DELETE {urlAstrea}/api/v2/feature-toggle/:feature/tenant/:tenantId`
 * depending on `operation` (`'ADD'` -> POST, `'REMOVE'` -> DELETE) — ported
 * 1:1 from the `method` lookup table in `featureToggleService.js`.
 *
 * The legacy `_execute()` caller only branched on the response's `status`
 * (204 -> success toast, anything else -> generic error toast) and always
 * cleared `$scope.target`/`$scope.feature` regardless of outcome; the same
 * status check is exposed here via the resolved value so
 * `FeatureTogglePage` can reproduce that exact toast copy.
 */
export function useFeatureToggleMutation() {
  const { urlAstreaPath: baseUrl } = useAstreaEnvironment()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ operation, feature, tenantId }) => {
      const method = { ADD: 'post', REMOVE: 'delete' }[operation]
      const response = await httpClient.request({
        method,
        url: `${baseUrl}/api/v2/feature-toggle/${feature}/tenant/${tenantId}`,
      })
      return response
    },
    onSuccess: (response, { operation, feature }) => {
      if (response.status === 204) {
        adjustTenantsCount(queryClient, feature, operation === 'ADD' ? 1 : -1)
      }
    },
  })
}

/**
 * Bulk-assign a feature to a list of tenants in one call:
 * `POST {urlAstrea}/api/v2/feature-toggle/:feature/tenant?overwrite=false`,
 * body `tenantIds: number[]`. `overwrite` is always `false` — this screen
 * only ever adds tenants to a feature, never replaces its existing tenant
 * list, so the flag is never exposed as a UI option.
 */
export function useFeatureToggleTenantsMutation() {
  const { urlAstreaPath: baseUrl } = useAstreaEnvironment()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ feature, tenantIds }) => {
      const response = await httpClient.post(
        `${baseUrl}/api/v2/feature-toggle/${feature}/tenant`,
        tenantIds,
        { params: { overwrite: false } },
      )
      return response
    },
    onSuccess: (response, { feature, tenantIds }) => {
      if (response.status === 204) {
        adjustTenantsCount(queryClient, feature, tenantIds.length)
      }
    },
  })
}

export function useCreateFeatureToggleMutation() {
  const { urlAstreaPath: baseUrl } = useAstreaEnvironment()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ name, description, plans, usageType }) => {
      const response = await httpClient.post(`${baseUrl}/api/v2/feature-toggle`, {
        name,
        description,
        plans,
        usageType,
        countries: ['BRAZIL'],
        enabled: true,
      })
      return response
    },
    onSuccess: (response, variables) => {
      if (isSuccessStatus(response.status)) {
        insertCreatedFeature(queryClient, variables)
      }
    },
  })
}

export function useUpdateFeatureToggleMutation() {
  const { urlAstreaPath: baseUrl } = useAstreaEnvironment()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ name, description, plans, usageType }) => {
      const response = await httpClient.put(`${baseUrl}/api/v2/feature-toggle`, {
        name,
        description,
        plans,
        usageType,
        countries: ['BRAZIL'],
        enabled: true,
      })
      return response
    },
    onSuccess: (response, variables) => {
      if (isSuccessStatus(response.status)) {
        applyFeatureEdit(queryClient, variables)
      }
    },
  })
}

export function useSaveFeatureToggleLogMutation() {
  return useMutation({
    mutationFn: async ({ who, feature, operation, target, tenantIds, reason }) => {
      const response = await httpClient.post('/statusLogs/create', {
        who,
        namespace: feature,
        oldStatus: operation,
        newStatus: JSON.stringify({ feature, operation, target, tenantIds, reason }),
      })
      return response
    },
  })
}
